import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Plus, Search, FileText, Eye, ChevronRight, ChevronLeft, DollarSign, Calendar, Download } from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../context/PermissionsContext';
import { DownloadWizard } from '../components/ui/download-wizard';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_TYPES = ['Land', 'Apartment', 'Hotel Share'];
const DISCOUNT_TYPES = [
  { value: 'none', label: 'No Discount' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'amount', label: 'Fixed Amount' }
];
const DOWNPAYMENT_TYPES = [
  { value: 'percentage', label: 'Percentage of Net' },
  { value: 'amount', label: 'Fixed Amount' }
];
const PAYMENT_FREQUENCIES = ['Monthly', 'Quarterly', 'Custom'];

export default function ContractsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({ full_name: '', phone: '', email: '' });
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editContractId, setEditContractId] = useState(null);
  
  const { role } = usePermissions();
  const isOwner = role === 'owner';

  // Contract download columns
  const CONTRACT_DOWNLOAD_COLUMNS = [
    { key: "file_id", label: "File ID" },
    { key: "bd_file_no", label: "BD File No" },
    { key: "customer_id", label: "Customer ID" },
    { key: "product_type", label: "Product Type" },
    { key: "project_name", label: "Project Name" },
    { key: "deal_name", label: "Deal Name" },
    { key: "gross_price", label: "Gross Price" },
    { key: "discount_amount", label: "Discount Amount" },
    { key: "net_price", label: "Net Price" },
    { key: "total_paid", label: "Total Paid" },
    { key: "total_due", label: "Total Due" },
    { key: "payment_status", label: "Payment Status" },
    { key: "created_at", label: "Created At" }
  ];

  // Download handler
  const handleDownload = async (selectedColumns) => {
    try {
      const response = await fetch(`${API_URL}/api/financial/contracts/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ columns: selectedColumns }),
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contracts_export_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Download started');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Download failed');
      }
    } catch (error) {
      logger.error('Error downloading:', error);
      toast.error('Download failed');
    }
  };

  const [formData, setFormData] = useState({
    // Step 1: Customer & Product Selection
    customer_id: searchParams.get('customer_id') || '',
    product_id: '',
    lead_id: '',
    file_id: '',  // Auto-generated, optional
    bd_file_no: '',  // Optional
    money_receipt_no: '',
    product_type: '',
    project_name: '',
    number_of_units: '',  // New: multiplied by unit_price = gross_price
    unit_price: '',  // From product selection
    // Step 2: Pricing
    gross_price: '',  // Auto-calculated: unit_price × number_of_units
    discount_type: 'none',
    discount_value: '',
    // Step 3: Payment Terms
    booking_money_amount: '',
    booking_money_date: '',
    downpayment_type: 'percentage',
    downpayment_value: '',
    downpayment_date: '',
    // Contract Type: one_time or installment
    contract_type: 'installment',
    one_time_payment_date: '',
    // Future Adjustment
    future_adjustments: [],
    // Step 4: Installment Schedule
    payment_period_months: '',
    payment_frequency: 'Monthly',
    installment_start_date: ''
    // number_of_installments is auto-calculated
  });
  const [computedValues, setComputedValues] = useState({});

  const fetchContracts = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      const customerId = searchParams.get('customer_id');
      if (customerId) params.append('customer_id', customerId);

      const response = await fetch(
        `${API_URL}/api/financial/contracts${params.toString() ? `?${params.toString()}` : ''}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setContracts(data);
      } else {
        toast.error('Failed to load contracts');
      }
    } catch (error) {
      logger.error('Error fetching contracts:', error);
      toast.error('Error loading contracts');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/financial/customers`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      }
    } catch (error) {
      logger.error('Error fetching customers:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/products/active/dropdown`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      logger.error('Error fetching products:', error);
    }
  };



  useEffect(() => {
    fetchContracts();
    fetchCustomers();
    fetchProducts();
  }, [searchQuery, statusFilter, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open wizard in edit mode when ?edit=contractId is in URL
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && customers.length > 0) {
      const loadContractForEdit = async () => {
        try {
          const res = await fetch(`${API_URL}/api/financial/contracts/${editId}`, { credentials: 'include' });
          if (res.ok) {
            const c = await res.json();
            setFormData({
              customer_id: c.customer_id || '',
              product_id: c.product_id || '',
              lead_id: c.lead_id || '',
              file_id: c.file_id || '',
              bd_file_no: c.bd_file_no || '',
              money_receipt_no: c.money_receipt_no || '',
              product_type: c.product_type || '',
              project_name: c.project_name || '',
              number_of_units: c.number_of_units ? c.number_of_units.toString() : '1',
              unit_price: c.unit_price ? c.unit_price.toString() : '',
              gross_price: c.gross_price ? c.gross_price.toString() : '',
              discount_type: c.discount_type || 'none',
              discount_value: c.discount_value ? c.discount_value.toString() : '',
              booking_money_amount: c.booking_money_amount ? c.booking_money_amount.toString() : '',
              booking_money_date: c.booking_money_date ? c.booking_money_date.split('T')[0] : '',
              downpayment_type: c.downpayment_type || 'percentage',
              downpayment_value: c.downpayment_value ? c.downpayment_value.toString() : '',
              downpayment_date: c.downpayment_date ? c.downpayment_date.split('T')[0] : '',
              future_adjustments: c.future_adjustments || [],
              contract_type: c.contract_type || 'installment',
              one_time_payment_date: c.one_time_payment_date ? c.one_time_payment_date.split('T')[0] : '',
              payment_period_months: c.payment_period_months ? c.payment_period_months.toString() : '',
              payment_frequency: c.payment_frequency || 'Monthly',
              installment_start_date: c.installment_start_date ? c.installment_start_date.split('T')[0] : '',
            });
            setEditMode(true);
            setEditContractId(editId);
            setShowCreateWizard(true);
            setWizardStep(1);
          }
        } catch (err) {
          logger.error('Failed to load contract for edit:', err);
        }
      };
      loadContractForEdit();
    }
  }, [searchParams, customers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-populate from product when selected
  const handleProductSelect = (productId) => {
    const product = products.find(p => p.product_id === productId);
    if (product) {
      setFormData(prev => ({
        ...prev,
        product_id: productId,
        project_name: product.project_name,
        product_type: product.product_category,
        unit_price: product.unit_price.toString(),
        number_of_units: '1',  // Default to 1 unit
        gross_price: product.unit_price.toString()  // Initial: 1 × unit_price
      }));
    } else {
      setFormData(prev => ({ ...prev, product_id: '', unit_price: '', number_of_units: '', gross_price: '' }));
    }
  };

  // Calculate gross price when number of units changes
  const handleUnitsChange = (units) => {
    const numUnits = parseInt(units) || 0;
    const unitPrice = parseFloat(formData.unit_price) || 0;
    const grossPrice = numUnits * unitPrice;
    setFormData(prev => ({
      ...prev,
      number_of_units: units,
      gross_price: grossPrice > 0 ? grossPrice.toString() : ''
    }));
  };

  // Auto-calculate number of installments based on payment period and frequency
  const calculateInstallments = (periodMonths, frequency) => {
    const period = parseInt(periodMonths) || 0;
    if (period <= 0) return 0;
    
    switch (frequency) {
      case 'Monthly':
        return period;
      case 'Quarterly':
        return Math.ceil(period / 3);
      case 'Custom':
        return period; // For custom, user can override
      default:
        return period;
    }
  };

  // Compute derived values when pricing changes
  useEffect(() => {
    const gross = parseFloat(formData.gross_price) || 0;
    const discountType = formData.discount_type;
    const discountValue = parseFloat(formData.discount_value) || 0;
    
    let discountAmount = 0;
    if (discountType === 'percentage') {
      discountAmount = gross * (discountValue / 100);
    } else if (discountType === 'amount') {
      discountAmount = discountValue;
    }
    
    const netPrice = gross - discountAmount;
    
    const bookingMoney = parseFloat(formData.booking_money_amount) || 0;
    const dpType = formData.downpayment_type;
    const dpValue = parseFloat(formData.downpayment_value) || 0;
    
    let downpaymentAmount = 0;
    if (dpType === 'percentage') {
      downpaymentAmount = netPrice * (dpValue / 100);
    } else {
      downpaymentAmount = dpValue;
    }
    
    const remainingAfterBooking = netPrice - bookingMoney;
    const remainingAfterDownpayment = remainingAfterBooking - downpaymentAmount;
    
    // Future Adjustment total
    const futureAdjustmentTotal = (formData.future_adjustments || []).reduce((sum, fa) => sum + (parseFloat(fa.amount) || 0), 0);
    const remainingForInstallments = remainingAfterDownpayment - futureAdjustmentTotal;
    
    // Auto-calculate installments from period and frequency
    const numInstallments = calculateInstallments(formData.payment_period_months, formData.payment_frequency);
    const installmentAmount = remainingForInstallments > 0 && numInstallments > 0 
      ? remainingForInstallments / numInstallments 
      : 0;

    setComputedValues({
      discountAmount: discountAmount.toFixed(2),
      netPrice: netPrice.toFixed(2),
      downpaymentAmount: downpaymentAmount.toFixed(2),
      remainingAfterBooking: remainingAfterBooking.toFixed(2),
      remainingAfterDownpayment: remainingAfterDownpayment.toFixed(2),
      futureAdjustmentTotal: futureAdjustmentTotal.toFixed(2),
      remainingForInstallments: remainingForInstallments.toFixed(2),
      installmentAmount: installmentAmount.toFixed(2),
      numInstallments: numInstallments
    });
  }, [formData]);

  // Create new customer inline
  const handleCreateCustomer = async () => {
    if (!newCustomerData.full_name || !newCustomerData.phone) {
      toast.error('Customer name and phone are required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/financial/customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newCustomerData)
      });

      if (response.ok) {
        const customer = await response.json();
        toast.success('Customer created');
        setFormData(prev => ({ ...prev, customer_id: customer.customer_id }));
        setShowCreateCustomer(false);
        setNewCustomerData({ full_name: '', phone: '', email: '' });
        fetchCustomers(); // Refresh customer list
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create customer');
      }
    } catch (error) {
      toast.error('Error creating customer');
    }
  };

  // Filter customers by search
  const filteredCustomers = customers.filter(c => 
    c.full_name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.phone?.includes(customerSearch)
  );

  const handleCreateContract = async () => {
    try {
      // Auto-generate file_id if not provided
      const fileId = formData.file_id || `GS-${Date.now().toString(36).toUpperCase()}`;
      
      const payload = {
        ...formData,
        file_id: fileId,
        gross_price: parseFloat(formData.gross_price),
        discount_value: parseFloat(formData.discount_value) || 0,
        booking_money_amount: parseFloat(formData.booking_money_amount) || 0,
        booking_money_date: formData.booking_money_date || null,
        downpayment_value: parseFloat(formData.downpayment_value) || 0,
        downpayment_date: formData.downpayment_date || null,
        number_of_units: formData.number_of_units ? parseInt(formData.number_of_units) : 1,
        unit_price: formData.unit_price ? parseFloat(formData.unit_price) : null,
        contract_type: formData.contract_type,
        one_time_payment_date: formData.contract_type === 'one_time' ? formData.one_time_payment_date : undefined,
        payment_period_months: formData.contract_type === 'one_time' ? 0 : parseInt(formData.payment_period_months),
        number_of_installments: formData.contract_type === 'one_time' ? 0 : (computedValues.numInstallments || calculateInstallments(formData.payment_period_months, formData.payment_frequency)),
        future_adjustments: formData.future_adjustments || [],
      };

      const response = await fetch(`${API_URL}/api/financial/contracts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Contract created with ${result.installments_created} installments`);
        setShowCreateWizard(false);
        setWizardStep(1);
        resetForm();
        fetchContracts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create contract');
      }
    } catch (error) {
      logger.error('Error creating contract:', error);
      toast.error('Error creating contract');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      product_id: '',
      lead_id: '',
      file_id: '',
      bd_file_no: '',
      money_receipt_no: '',
      product_type: '',
      project_name: '',
      number_of_units: '',
      unit_price: '',
      gross_price: '',
      discount_type: 'none',
      discount_value: '',
      booking_money_amount: '',
      booking_money_date: '',
      downpayment_type: 'percentage',
      downpayment_value: '',
      downpayment_date: '',
      contract_type: 'installment',
      one_time_payment_date: '',
      future_adjustments: [],
      payment_period_months: '',
      payment_frequency: 'Monthly',
      installment_start_date: ''
    });
    setCustomerSearch('');
    setEditMode(false);
    setEditContractId(null);
  };

  const handleEditContract = async () => {
    // Destructive action confirmation: editing financial fields wipes ALL
    // installments and re-allocates existing payments into the new schedule.
    const confirmed = window.confirm(
      "This will rebuild ALL installments and re-allocate existing payments " +
      "(oldest first) into the new schedule.\n\n" +
      "• Booking Money + Down Payment stay in their own buckets.\n" +
      "• Installment payments fill the new schedule chronologically.\n" +
      "• Payment ledger (amounts, dates) is preserved as-is.\n\n" +
      "Continue?"
    );
    if (!confirmed) return;
    try {
      const payload = {
        file_id: formData.file_id,
        bd_file_no: formData.bd_file_no,
        money_receipt_no: formData.money_receipt_no,
        product_type: formData.product_type,
        project_name: formData.project_name,
        deal_name: `${formData.project_name || ''} - ${formData.product_type || ''}`,
        gross_price: parseFloat(formData.gross_price) || 0,
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value) || 0,
        booking_money_amount: parseFloat(formData.booking_money_amount) || 0,
        booking_money_date: formData.booking_money_date || null,
        downpayment_type: formData.downpayment_type,
        downpayment_value: parseFloat(formData.downpayment_value) || 0,
        downpayment_date: formData.downpayment_date || null,
        future_adjustments: formData.future_adjustments || [],
        contract_type: formData.contract_type,
        one_time_payment_date: formData.contract_type === 'one_time' ? formData.one_time_payment_date : undefined,
        payment_period_months: formData.contract_type === 'one_time' ? 0 : (parseInt(formData.payment_period_months) || 12),
        payment_frequency: formData.payment_frequency,
        // Send raw YYYY-MM-DD (matching create flow). Wrapping in
        // new Date().toISOString() converts to UTC midnight which then
        // renders as the previous calendar day in any timezone west of UTC,
        // causing the "I picked 4/20 but it shows 4/19" bug on edit.
        installment_start_date: formData.installment_start_date || undefined,
        number_of_units: formData.number_of_units ? parseInt(formData.number_of_units) : 1,
        unit_price: formData.unit_price ? parseFloat(formData.unit_price) : null,
      };

      const response = await fetch(`${API_URL}/api/financial/contracts/${editContractId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Contract updated successfully! Installments recalculated.');
        setShowCreateWizard(false);
        resetForm();
        fetchContracts();
        // Navigate back to contract detail
        navigate(`/finance/contracts/${editContractId}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update contract');
      }
    } catch (error) {
      logger.error('Error updating contract:', error);
      toast.error('Error updating contract');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Active': 'bg-green-100 text-green-800',
      'Overdue': 'bg-red-100 text-red-800',
      'Completed': 'bg-blue-100 text-blue-800',
      'Cancelled': 'bg-slate-100 text-slate-800'
    };
    return (
      <Badge className={variants[status] || 'bg-slate-100 text-slate-800'}>
        {status}
      </Badge>
    );
  };

  const isOneTime = formData.contract_type === 'one_time';
  const totalSteps = isOneTime ? 3 : 4;

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.customer_id && formData.product_type;
      case 2:
        return formData.gross_price && parseFloat(formData.gross_price) > 0;
      case 3:
        if (isOneTime) {
          return formData.one_time_payment_date;
        }
        return formData.booking_money_amount && parseFloat(formData.booking_money_amount) >= 0;
      case 4:
        return formData.payment_period_months && formData.installment_start_date && 
               parseInt(formData.payment_period_months) > 0;
      default:
        return false;
    }
  };

  // Get selected customer name for display
  const selectedCustomer = customers.find(c => c.customer_id === formData.customer_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contracts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Financial Contracts</h1>
          <p className="text-slate-600 font-body">Manage customer contracts and installments</p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Download Button - Owner Only */}
          {isOwner && (
            <Button
              variant="outline"
              onClick={() => setDownloadOpen(true)}
              data-testid="download-contracts-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          )}
          
          <Dialog open={showCreateWizard} onOpenChange={(open) => {
            setShowCreateWizard(open);
            if (!open) {
              setWizardStep(1);
              resetForm();
              // Clear edit param from URL if present
              if (searchParams.get('edit')) {
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('edit');
                navigate(`/finance/contracts${newParams.toString() ? `?${newParams.toString()}` : ''}`, { replace: true });
              }
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-gold hover:bg-gold/90 text-slate-900" data-testid="create-contract-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Contract
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit' : 'Create'} Contract - Step {wizardStep} of {totalSteps}</DialogTitle>
            </DialogHeader>
            
            {/* Progress Indicator */}
            <div className="flex items-center justify-between mb-6">
              {[1, 2, 3, 4].filter(s => isOneTime ? s <= 3 : true).map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                    ${wizardStep >= step ? 'bg-gold text-slate-900' : 'bg-slate-200 text-slate-500'}`}>
                    {step}
                  </div>
                  {step < totalSteps && (
                    <div className={`w-12 sm:w-20 h-1 mx-1 ${wizardStep > step ? 'bg-gold' : 'bg-slate-200'}`} />
                  )}
                </div>
              ))}
            </div>

            {/* Step 1: Customer & Product Selection */}
            {wizardStep === 1 && (
              <div className="space-y-4">
                <h3 className="font-medium text-lg">Customer & Product Selection</h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* Customer Search Box */}
                  <div className="col-span-2">
                    <Label>Customer *</Label>
                    {formData.customer_id && selectedCustomer ? (
                      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                        <span className="font-medium">{selectedCustomer.full_name}</span>
                        <span className="text-slate-500">({selectedCustomer.phone})</span>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          className="ml-auto text-red-600 hover:text-red-700"
                          onClick={() => setFormData({ ...formData, customer_id: '' })}
                        >
                          Change
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Input
                          placeholder="Search by name or phone..."
                          value={customerSearch}
                          onChange={(e) => setCustomerSearch(e.target.value)}
                          data-testid="customer-search-input"
                        />
                        {customerSearch && (
                          <div className="max-h-40 overflow-y-auto border rounded-md divide-y">
                            {filteredCustomers.length === 0 ? (
                              <div className="p-3 text-center text-slate-500">
                                No customers found
                              </div>
                            ) : (
                              filteredCustomers.slice(0, 5).map((c) => (
                                <button
                                  key={c.customer_id}
                                  type="button"
                                  className="w-full p-2 text-left hover:bg-slate-50 flex justify-between items-center"
                                  onClick={() => {
                                    setFormData({ ...formData, customer_id: c.customer_id });
                                    setCustomerSearch('');
                                  }}
                                >
                                  <span className="font-medium">{c.full_name}</span>
                                  <span className="text-sm text-slate-500">{c.phone}</span>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => setShowCreateCustomer(true)}
                          className="w-full"
                        >
                          <Plus className="h-4 w-4 mr-1" /> Add New Customer
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-slate-500 mt-1">Same customer can have multiple contracts</p>
                  </div>
                  
                  {/* Inline Create Customer Form */}
                  {showCreateCustomer && (
                    <div className="col-span-2 p-4 bg-slate-50 rounded-lg border space-y-3">
                      <div className="flex justify-between items-center">
                        <Label className="text-base font-medium">New Customer</Label>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setShowCreateCustomer(false)}>Cancel</Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          placeholder="Full Name *"
                          value={newCustomerData.full_name}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, full_name: e.target.value })}
                        />
                        <Input
                          placeholder="Phone *"
                          value={newCustomerData.phone}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: e.target.value })}
                        />
                        <Input
                          placeholder="Email (optional)"
                          value={newCustomerData.email}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                          className="col-span-2"
                        />
                      </div>
                      <Button type="button" size="sm" onClick={handleCreateCustomer} className="bg-gold hover:bg-gold/90 text-slate-900">
                        Create & Select
                      </Button>
                    </div>
                  )}
                  
                  {/* Product Dropdown */}
                  <div className="col-span-2">
                    <Label>Product (Optional - auto-populates fields)</Label>
                    <Select value={formData.product_id || "manual"} onValueChange={handleProductSelect}>
                      <SelectTrigger data-testid="product-select">
                        <SelectValue placeholder="Select product or enter manually" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manual">Enter manually</SelectItem>
                        {products.map((p) => (
                          <SelectItem key={p.product_id} value={p.product_id}>
                            {p.project_name} ({p.product_category}) - ${p.unit_price?.toLocaleString()}/unit
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Number of Units - shown when product is selected */}
                  {formData.product_id && formData.product_id !== 'manual' && (
                    <div className="col-span-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Unit Price</Label>
                          <div className="h-10 px-3 py-2 bg-white rounded-md border flex items-center font-medium text-slate-700">
                            ${parseFloat(formData.unit_price || 0).toLocaleString()}
                          </div>
                        </div>
                        <div>
                          <Label>Number of Units *</Label>
                          <Input 
                            type="number" 
                            min="1"
                            value={formData.number_of_units} 
                            onChange={(e) => handleUnitsChange(e.target.value)}
                            placeholder="e.g., 1"
                            data-testid="number-of-units-input"
                          />
                        </div>
                        <div>
                          <Label>Total Gross Price</Label>
                          <div className="h-10 px-3 py-2 bg-gold/20 rounded-md border border-gold flex items-center font-bold text-slate-900">
                            ${parseFloat(formData.gross_price || 0).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-blue-600 mt-2">
                        Calculation: ${parseFloat(formData.unit_price || 0).toLocaleString()} × {formData.number_of_units || 0} units = ${parseFloat(formData.gross_price || 0).toLocaleString()}
                      </p>
                    </div>
                  )}
                  
                  <div>
                    <Label>File ID (Optional - auto-generated)</Label>
                    <Input value={formData.file_id} onChange={(e) => setFormData({ ...formData, file_id: e.target.value })} 
                           placeholder="Leave blank to auto-generate" data-testid="file-id-input" />
                  </div>
                  <div>
                    <Label>BD File No (Optional)</Label>
                    <Input value={formData.bd_file_no} onChange={(e) => setFormData({ ...formData, bd_file_no: e.target.value })} 
                           placeholder="Bangladesh file number" />
                  </div>
                  <div>
                    <Label>Money Receipt No</Label>
                    <Input value={formData.money_receipt_no} onChange={(e) => setFormData({ ...formData, money_receipt_no: e.target.value })} 
                           placeholder="Receipt number (optional)" />
                  </div>
                  <div>
                    <Label>Product Type *</Label>
                    <Select value={formData.product_type} onValueChange={(v) => setFormData({ ...formData, product_type: v })}>
                      <SelectTrigger data-testid="product-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {PRODUCT_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Project Name</Label>
                    <Input value={formData.project_name} onChange={(e) => setFormData({ ...formData, project_name: e.target.value })} 
                           placeholder="Optional project name" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Pricing */}
            {wizardStep === 2 && (
              <div className="space-y-4">
                <h3 className="font-medium text-lg">Pricing & Discounts</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Gross Price (USD) *</Label>
                    <Input type="number" value={formData.gross_price} 
                           onChange={(e) => setFormData({ ...formData, gross_price: e.target.value })} 
                           placeholder="Total price before discount" data-testid="gross-price-input" />
                  </div>
                  <div>
                    <Label>Discount Type</Label>
                    <Select value={formData.discount_type} onValueChange={(v) => setFormData({ ...formData, discount_type: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select discount type" />
                      </SelectTrigger>
                      <SelectContent>
                        {DISCOUNT_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Discount Value {formData.discount_type === 'percentage' ? '(%)' : '(USD)'}</Label>
                    <Input type="number" value={formData.discount_value} 
                           onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })} 
                           placeholder={formData.discount_type === 'percentage' ? 'e.g., 10' : 'e.g., 5000'}
                           disabled={formData.discount_type === 'none'} />
                  </div>
                </div>
                
                {/* Computed Values Preview */}
                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Gross Price:</span>
                    <span className="font-medium">${parseFloat(formData.gross_price || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Discount:</span>
                    <span className="font-medium text-green-600">- ${parseFloat(computedValues.discountAmount || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-base font-semibold border-t pt-2">
                    <span>Net Price:</span>
                    <span className="text-gold">${parseFloat(computedValues.netPrice || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Payment Terms */}
            {wizardStep === 3 && (
              <div className="space-y-4">
                <h3 className="font-medium text-lg">Payment Terms</h3>

                {/* Contract Type Toggle */}
                <div className="flex gap-2 p-1 bg-slate-100 rounded-lg" data-testid="contract-type-toggle">
                  <button
                    type="button"
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      formData.contract_type === 'one_time'
                        ? 'bg-white shadow text-slate-900'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    onClick={() => setFormData({ ...formData, contract_type: 'one_time' })}
                  >
                    One-Time Payment
                  </button>
                  <button
                    type="button"
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      formData.contract_type === 'installment'
                        ? 'bg-white shadow text-slate-900'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    onClick={() => setFormData({ ...formData, contract_type: 'installment' })}
                  >
                    Installment Plan
                  </button>
                </div>

                {/* One-Time Payment Mode */}
                {isOneTime && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label>Payment Due Date *</Label>
                        <Input type="date" value={formData.one_time_payment_date}
                          onChange={(e) => setFormData({ ...formData, one_time_payment_date: e.target.value })}
                          data-testid="one-time-date-input" />
                      </div>
                      <div className="col-span-2">
                        <Label>Booking Money (USD)</Label>
                        <Input type="number" value={formData.booking_money_amount}
                          onChange={(e) => setFormData({ ...formData, booking_money_amount: e.target.value })}
                          placeholder="Optional booking amount" data-testid="booking-money-input" />
                      </div>
                      {parseFloat(formData.booking_money_amount || 0) > 0 && (
                        <div className="col-span-2">
                          <Label>Booking Money Receivable Date</Label>
                          <Input type="date" value={formData.booking_money_date}
                            onChange={(e) => setFormData({ ...formData, booking_money_date: e.target.value })}
                            data-testid="booking-money-date-input" />
                        </div>
                      )}
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Net Price:</span>
                        <span className="font-medium">${parseFloat(computedValues.netPrice || 0).toLocaleString()}</span>
                      </div>
                      {parseFloat(formData.booking_money_amount || 0) > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Booking Money:</span>
                          <span className="font-medium text-blue-600">- ${parseFloat(formData.booking_money_amount || 0).toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-base font-semibold border-t pt-2">
                        <span>Total Due (One-Time):</span>
                        <span className="text-gold">${parseFloat(computedValues.remainingForInstallments || computedValues.netPrice || 0).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Installment Mode */}
                {!isOneTime && (
                <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Booking Money (USD) *</Label>
                    <Input type="number" value={formData.booking_money_amount} 
                           onChange={(e) => setFormData({ ...formData, booking_money_amount: e.target.value })} 
                           placeholder="Initial booking amount" data-testid="booking-money-input" />
                  </div>
                  <div>
                    <Label>Booking Money Date</Label>
                    <Input type="date" value={formData.booking_money_date}
                           onChange={(e) => setFormData({ ...formData, booking_money_date: e.target.value })}
                           data-testid="booking-money-date-input" />
                  </div>
                  <div>
                    <Label>Down Payment Type</Label>
                    <Select value={formData.downpayment_type} onValueChange={(v) => setFormData({ ...formData, downpayment_type: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {DOWNPAYMENT_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Down Payment Value {formData.downpayment_type === 'percentage' ? '(%)' : '(USD)'}</Label>
                    <Input type="number" value={formData.downpayment_value} 
                           onChange={(e) => setFormData({ ...formData, downpayment_value: e.target.value })} 
                           placeholder={formData.downpayment_type === 'percentage' ? 'e.g., 20' : 'e.g., 10000'} />
                  </div>
                  {(parseFloat(formData.downpayment_value || 0) > 0) && (
                    <div className="col-span-2">
                      <Label>Down Payment Receivable Date</Label>
                      <Input type="date" value={formData.downpayment_date}
                             onChange={(e) => setFormData({ ...formData, downpayment_date: e.target.value })}
                             data-testid="downpayment-date-input" />
                    </div>
                  )}
                </div>

                {/* Computed Values Preview */}
                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Net Price:</span>
                    <span className="font-medium">${parseFloat(computedValues.netPrice || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Booking Money:</span>
                    <span className="font-medium text-blue-600">- ${parseFloat(formData.booking_money_amount || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Down Payment:</span>
                    <span className="font-medium text-blue-600">- ${parseFloat(computedValues.downpaymentAmount || 0).toLocaleString()}</span>
                  </div>
                  {parseFloat(computedValues.futureAdjustmentTotal || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Future Adjustment:</span>
                      <span className="font-medium text-purple-600">- ${parseFloat(computedValues.futureAdjustmentTotal || 0).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-base font-semibold border-t pt-2">
                    <span>Remaining for Installments:</span>
                    <span className="text-gold">${parseFloat(computedValues.remainingForInstallments || computedValues.remainingAfterDownpayment || 0).toLocaleString()}</span>
                  </div>
                </div>

                {/* Future Adjustment Section */}
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Future Adjustments (Optional)</Label>
                    <Button type="button" variant="outline" size="sm" onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        future_adjustments: [...(prev.future_adjustments || []), { label: '', amount: '', type: 'one_time', date: '', payments: [] }]
                      }));
                    }} data-testid="add-future-adjustment">
                      + Add Adjustment
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">Fixed amounts the client will adjust in future. Deducted from installment calculation.</p>
                  
                  {(formData.future_adjustments || []).map((fa, faIdx) => (
                    <div key={fa.label ? `fa-${fa.label}-${faIdx}` : `fa-new-${faIdx}`} className="border rounded-lg p-3 space-y-3 bg-slate-50/50" data-testid={`fa-${faIdx}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-700">Adjustment {faIdx + 1}</span>
                        <Button type="button" variant="ghost" size="sm" className="text-red-500 h-7 px-2" onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            future_adjustments: prev.future_adjustments.filter((_, i) => i !== faIdx)
                          }));
                        }}>Remove</Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Label</Label>
                          <Input placeholder="e.g., Land Transfer Credit" value={fa.label}
                            onChange={(e) => {
                              const updated = [...formData.future_adjustments];
                              updated[faIdx] = { ...updated[faIdx], label: e.target.value };
                              setFormData(prev => ({ ...prev, future_adjustments: updated }));
                            }} />
                        </div>
                        <div>
                          <Label className="text-xs">Total Amount (USD)</Label>
                          <Input type="number" placeholder="0" value={fa.amount}
                            onChange={(e) => {
                              const updated = [...formData.future_adjustments];
                              updated[faIdx] = { ...updated[faIdx], amount: e.target.value };
                              setFormData(prev => ({ ...prev, future_adjustments: updated }));
                            }} />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Payment Mode</Label>
                        <Select value={fa.type || 'one_time'} onValueChange={(v) => {
                          const updated = [...formData.future_adjustments];
                          updated[faIdx] = { ...updated[faIdx], type: v, payments: v === 'multiple' ? [{ amount: '', date: '' }] : [] };
                          setFormData(prev => ({ ...prev, future_adjustments: updated }));
                        }}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="one_time">One-Time Payment</SelectItem>
                            <SelectItem value="multiple">Multiple Payments</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {fa.type === 'one_time' && (
                        <div>
                          <Label className="text-xs">Due Date</Label>
                          <Input type="date" value={fa.date || ''}
                            onChange={(e) => {
                              const updated = [...formData.future_adjustments];
                              updated[faIdx] = { ...updated[faIdx], date: e.target.value };
                              setFormData(prev => ({ ...prev, future_adjustments: updated }));
                            }} />
                        </div>
                      )}
                      
                      {fa.type === 'multiple' && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs">Payment Schedule</Label>
                            <Button type="button" variant="ghost" size="sm" className="h-6 text-xs" onClick={() => {
                              const updated = [...formData.future_adjustments];
                              updated[faIdx] = { ...updated[faIdx], payments: [...(updated[faIdx].payments || []), { amount: '', date: '' }] };
                              setFormData(prev => ({ ...prev, future_adjustments: updated }));
                            }}>+ Add Payment</Button>
                          </div>
                          {(fa.payments || []).map((p, pIdx) => (
                            <div key={p.date || `pay-${pIdx}`} className="flex gap-2 items-end">
                              <div className="flex-1">
                                <Label className="text-[10px] text-slate-400">Amount</Label>
                                <Input type="number" placeholder="0" value={p.amount} className="h-8 text-sm"
                                  onChange={(e) => {
                                    const updated = [...formData.future_adjustments];
                                    const payments = [...updated[faIdx].payments];
                                    payments[pIdx] = { ...payments[pIdx], amount: e.target.value };
                                    updated[faIdx] = { ...updated[faIdx], payments };
                                    setFormData(prev => ({ ...prev, future_adjustments: updated }));
                                  }} />
                              </div>
                              <div className="flex-1">
                                <Label className="text-[10px] text-slate-400">Due Date</Label>
                                <Input type="date" value={p.date || ''} className="h-8 text-sm"
                                  onChange={(e) => {
                                    const updated = [...formData.future_adjustments];
                                    const payments = [...updated[faIdx].payments];
                                    payments[pIdx] = { ...payments[pIdx], date: e.target.value };
                                    updated[faIdx] = { ...updated[faIdx], payments };
                                    setFormData(prev => ({ ...prev, future_adjustments: updated }));
                                  }} />
                              </div>
                              <Button type="button" variant="ghost" size="sm" className="h-8 text-red-400 px-1"
                                onClick={() => {
                                  const updated = [...formData.future_adjustments];
                                  updated[faIdx] = { ...updated[faIdx], payments: updated[faIdx].payments.filter((_, i) => i !== pIdx) };
                                  setFormData(prev => ({ ...prev, future_adjustments: updated }));
                                }}>x</Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                </>
                )}
              </div>
            )}

            {/* Step 4: Installment Schedule */}
            {wizardStep === 4 && (
              <div className="space-y-4">
                <h3 className="font-medium text-lg">Installment Schedule</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Payment Period (Months) *</Label>
                    <Input type="number" value={formData.payment_period_months} 
                           onChange={(e) => setFormData({ ...formData, payment_period_months: e.target.value })} 
                           placeholder="e.g., 24" data-testid="payment-period-input" />
                  </div>
                  <div>
                    <Label>Payment Frequency</Label>
                    <Select value={formData.payment_frequency} onValueChange={(v) => setFormData({ ...formData, payment_frequency: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_FREQUENCIES.map((freq) => (
                          <SelectItem key={freq} value={freq}>{freq}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Installment Start Date *</Label>
                    <Input type="date" value={formData.installment_start_date} 
                           onChange={(e) => setFormData({ ...formData, installment_start_date: e.target.value })} 
                           data-testid="start-date-input" />
                  </div>
                  <div>
                    <Label>Number of Installments (Auto-calculated)</Label>
                    <div className="h-10 px-3 py-2 bg-slate-100 rounded-md border flex items-center font-medium">
                      {computedValues.numInstallments || 0}
                      <span className="ml-2 text-xs text-slate-500">
                        ({formData.payment_period_months || 0} months / {formData.payment_frequency === 'Quarterly' ? '3' : '1'} = {computedValues.numInstallments || 0})
                      </span>
                    </div>
                  </div>
                </div>

                {/* Final Summary */}
                <div className="bg-green-50 p-4 rounded-lg border border-green-200 space-y-2">
                  <h4 className="font-semibold text-green-800">Contract Summary</h4>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Net Price:</span>
                    <span className="font-medium">${parseFloat(computedValues.netPrice || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Booking Money:</span>
                    <span className="font-medium">- ${parseFloat(formData.booking_money_amount || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Down Payment:</span>
                    <span className="font-medium">- ${parseFloat(computedValues.downpaymentAmount || 0).toLocaleString()}</span>
                  </div>
                  {parseFloat(computedValues.futureAdjustmentTotal || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Future Adjustment:</span>
                      <span className="font-medium text-purple-600">- ${parseFloat(computedValues.futureAdjustmentTotal || 0).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm border-t pt-2 mt-2">
                    <span className="text-slate-600">Remaining for Installments:</span>
                    <span className="font-medium">${parseFloat(computedValues.remainingForInstallments || computedValues.remainingAfterDownpayment || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Number of Installments:</span>
                    <span className="font-medium">{computedValues.numInstallments || 0}</span>
                  </div>
                  <div className="flex justify-between text-base font-bold text-green-800 border-t pt-2 mt-2">
                    <span>Each Installment:</span>
                    <span>${parseFloat(computedValues.installmentAmount || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-6">
              <Button 
                variant="outline" 
                onClick={() => setWizardStep(Math.max(1, wizardStep - 1))}
                disabled={wizardStep === 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              
              {wizardStep < totalSteps ? (
                <Button 
                  onClick={() => setWizardStep(wizardStep + 1)}
                  disabled={!validateStep(wizardStep)}
                  className="bg-gold hover:bg-gold/90 text-slate-900"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button 
                  onClick={editMode ? handleEditContract : handleCreateContract}
                  disabled={!validateStep(wizardStep)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="submit-contract-btn"
                >
                  {editMode ? 'Save Changes' : 'Create Contract'}
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by file ID, project name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="contract-search-input"
              />
            </div>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Overdue">Overdue</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
                <SelectItem value="Cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Contracts Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-body">
            {contracts.length} Contract{contracts.length !== 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {contracts.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No contracts found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File ID</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Net Price</TableHead>
                    <TableHead>Outstanding</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contracts.map((contract) => (
                    <TableRow key={contract.contract_id} data-testid={`contract-row-${contract.contract_id}`}>
                      <TableCell className="font-mono text-sm">{contract.file_id}</TableCell>
                      <TableCell>{contract.customer_name || 'Unknown'}</TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium">{contract.project_name || contract.deal_name}</span>
                          <Badge variant="outline" className="ml-2 text-xs">{contract.product_type}</Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3 text-slate-400" />
                          {parseFloat(contract.net_price || 0).toLocaleString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={contract.outstanding_balance > 0 ? 'text-amber-600 font-medium' : 'text-green-600'}>
                          ${parseFloat(contract.outstanding_balance || 0).toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell>{getStatusBadge(contract.payment_status)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/finance/contracts/${contract.contract_id}`)}
                          data-testid={`view-contract-${contract.contract_id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Download Wizard - Owner Only */}
      {isOwner && (
        <DownloadWizard
          open={downloadOpen}
          onOpenChange={setDownloadOpen}
          title="Download Contracts"
          description="Select the columns you want to include in the contracts export."
          columns={CONTRACT_DOWNLOAD_COLUMNS}
          onDownload={handleDownload}
          defaultSelected={CONTRACT_DOWNLOAD_COLUMNS.map(c => c.key)}
        />
      )}
    </div>
  );
}

